/**
 * The only place that touches browser storage. Every read and write is guarded:
 * storage can be missing (private mode) or full, and the app must keep working.
 */
import { coerceCurrency } from './domain';

const PREFIX = 'cardnd.';

export function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writeStored<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* storage unavailable: the preference only lasts for this visit */
  }
}

/*
 * Raw keys, without the `cardnd.` prefix: the session of the current site lives under
 * `jwt_token` (raw string) and `auth_client` (JSON), see docs/legacy-audit/STORAGE.md L1/L2.
 * Keeping those exact keys keeps signed-in users of the old site signed in after the switch.
 */

export function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeRaw(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the session only lasts for this visit */
  }
}

export function removeRaw(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to remove */
  }
}

/*
 * Keys of the old site handled once at boot (docs/legacy-audit/STORAGE.md, Decision 21):
 *   L3 `client`            legacy client object (personal data), never written: removed
 *   L6 `draftReservation`  the old checkout draft (name, e-mail, phone, licence and passport numbers): removed
 *   L5 `selectedCurrency`  the visitor's currency (raw `MAD|EUR|USD`): read once, coerced, kept under
 *                          `cardnd.currency` when that key is not set yet (the legacy key itself is left alone)
 * Every other key is kept.
 */
export const LEGACY_REMOVED_AT_BOOT = ['client', 'draftReservation'] as const;
export const LEGACY_CURRENCY_KEY = 'selectedCurrency';
export const CURRENCY_KEY = 'currency';

/** Boot, before the first render (`main.tsx`, via `services/bootStorage`). */
export function migrateLegacyStorage(): void {
  for (const key of LEGACY_REMOVED_AT_BOOT) removeRaw(key);
  if (readRaw(PREFIX + CURRENCY_KEY) !== null) return;
  const legacy = readRaw(LEGACY_CURRENCY_KEY);
  if (legacy !== null) writeStored(CURRENCY_KEY, coerceCurrency(legacy.trim()));
}

/** Per-tab raw keys (sessionStorage, no prefix): legacy flags such as `rating_modal_shown_<clientId>` (S3). */
export function readRawSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeRawSession(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the flag only lasts for this page */
  }
}

/** Per-tab values (sessionStorage, prefixed): e.g. the account id between «mot de passe oublié» and the reset. */
export function readSession<T>(key: string, fallback: T): T {
  try {
    const raw = window.sessionStorage.getItem(PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writeSession<T>(key: string, value: T): void {
  try {
    window.sessionStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}
